import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Film, Palette, Camera, Megaphone, Star, Calendar, ChevronRight, Play } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MediaPage = () => {
  const [bookingForm, setBookingForm] = useState({
    service_type: 'media_consultation',
    brand: 'media',
    date: '',
    time_slot: '',
    name: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);
  const [quoteSubmitted, setQuoteSubmitted] = useState(false);
  const [quoteForm, setQuoteForm] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    budget: '',
    details: ''
  });

  // Media Gallery Videos
  const mediaVideos = [
    { id: 1, title: 'Brand Story', type: 'youtube', url: 'https://www.youtube.com/embed/KHLeFAzVuJU', thumbnail: 'https://customer-assets.emergentagent.com/job_build-site-461/artifacts/scwopwdg_Filmmaker%20picture.jpg' },
    { id: 2, title: 'Creative Process', type: 'drive', url: 'https://drive.google.com/file/d/1UbUnlr7SJgWkSV8OPu83JIXNIbMVAymH/preview', thumbnail: 'https://images.unsplash.com/photo-1761370980985-c743df189375?w=800' },
    { id: 3, title: 'Client Success', type: 'drive', url: 'https://drive.google.com/file/d/1Gr_c1I1TZ9HgzWoZc3vysO56sjnKiuRJ/preview', thumbnail: 'https://images.unsplash.com/photo-1770894807553-e467f5c048d3?w=800' },
    { id: 4, title: 'Behind The Scenes', type: 'drive', url: 'https://drive.google.com/file/d/1qJfHFrdX9o8SlpE0iyw93Dazz2i-Z-j5/preview', thumbnail: 'https://images.unsplash.com/photo-1640498293268-c7151c778c30?w=800' },
    { id: 5, title: 'Project Showcase', type: 'drive', url: 'https://drive.google.com/file/d/19CTruVFc5hOJzB_SF3YeiHeH_albiukj/preview', thumbnail: 'https://images.unsplash.com/photo-1690237571133-0e1ac3024155?w=800' },
    { id: 6, title: 'Film Reel', type: 'drive', url: 'https://drive.google.com/file/d/1WhXmgcX59Ycq88fuot9H6S7H157tS7Nm/preview', thumbnail: 'https://images.unsplash.com/photo-1748558894517-5ca245442811?w=800' },
    { id: 7, title: 'Featured Work', type: 'drive', url: 'https://drive.google.com/file/d/1CYxsoIJknwj_oCIkalreV05MLRvPJD0y/preview', thumbnail: 'https://images.unsplash.com/photo-1690237556610-bde1824612d0?w=800' },
  ];

  const services = [
    { icon: Film, title: 'Film Production', desc: 'From concept to screen, we bring your stories to life with cinematic excellence.' },
    { icon: Camera, title: 'Photography', desc: 'Professional photography services for brands, events, and creative projects.' },
    { icon: Palette, title: 'Branding & Design', desc: 'Comprehensive brand identity development and visual design services.' },
    { icon: Megaphone, title: 'Consultancy', desc: 'Expert guidance on media strategy, content creation, and brand positioning.' }
  ];

  const portfolio = [
    { title: 'Luxury Brand Campaign', category: 'Film', image: 'https://images.pexels.com/photos/3379943/pexels-photo-3379943.jpeg' },
    { title: 'Corporate Identity', category: 'Branding', image: 'https://images.unsplash.com/photo-1761701391167-863560b27f5e?w=600' },
    { title: 'Fashion Editorial', category: 'Photography', image: 'https://images.unsplash.com/photo-1760726433630-904f1a4f46ab?w=600' },
    { title: 'Product Launch', category: 'Film', image: 'https://images.pexels.com/photos/6839656/pexels-photo-6839656.jpeg' },
  ];

  const rateCards = [
    { service: 'Film Production', starting: 5000, note: 'Per project, varies by scope' },
    { service: 'Photography Session', starting: 500, note: '4-hour session, includes editing' },
    { service: 'Brand Identity Package', starting: 2500, note: 'Logo, guidelines, collateral' },
    { service: 'Consultation', starting: 150, note: 'Per hour' },
  ];

  const reviews = [
    { name: 'Creative Studios', rating: 5, comment: 'Outstanding cinematography for our brand campaign. Exceeded expectations!', service: 'Film Production' },
    { name: 'Tech Startup Inc.', rating: 5, comment: 'Their branding expertise transformed our company identity.', service: 'Branding' }
  ];

  const timeSlots = ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM', '04:00 PM'];

  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);
  const minDateStr = minDate.toISOString().split('T')[0];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, bookingForm);
      setSubmitted(true);
      toast.success('Consultation booked! Check your email for confirmation.');
    } catch (error) {
      toast.error('Failed to book consultation');
    } finally {
      setLoading(false);
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/quote-request`, {
        ...quoteForm,
        recipient: 'admin@arteryvailofficial.com',
        subject: 'New Quote Request - ARTERYVAIL Media'
      });
      setQuoteSubmitted(true);
      toast.success('Quote request sent! We\'ll respond within 24 hours.');
    } catch (error) {
      toast.error('Failed to send quote request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20">
      {/* Hero with Video Background (Desktop) / Image (Mobile) */}
      <section className="relative h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          {/* Video background - hidden on mobile, shown on desktop */}
          <iframe
            src="https://www.youtube.com/embed/KHLeFAzVuJU?autoplay=1&mute=1&loop=1&playlist=KHLeFAzVuJU&controls=0&showinfo=0&rel=0&modestbranding=1"
            className="hidden md:block absolute top-1/2 left-1/2 w-[177.77777778vh] h-[56.25vw] min-w-full min-h-full -translate-x-1/2 -translate-y-1/2"
            frameBorder="0"
            allow="autoplay; loop"
            title="ARTERYVAIL Media Background"
          />
          {/* Mobile background image */}
          <img
            src="https://customer-assets.emergentagent.com/job_build-site-461/artifacts/scwopwdg_Filmmaker%20picture.jpg"
            alt="ARTERYVAIL Media"
            className="md:hidden w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/95 via-[#0A0A0A]/70 to-transparent" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <p className="text-sm tracking-[0.3em] uppercase text-[#FAD6C6] mb-4">ARTERYVAIL Media</p>
            <h1 className="font-serif text-5xl md:text-7xl text-[#F5F2EB] mb-6">
              Cinematic Excellence, Crafted Stories
            </h1>
            <p className="text-lg text-[#A3988F] leading-relaxed mb-8">
              From filmmaking to branding, we transform your vision into compelling visual narratives.
            </p>
            <a href="#portfolio" className="btn-primary inline-flex items-center space-x-3" data-testid="media-view-work-btn">
              <span>View Our Work</span>
              <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-sm tracking-[0.3em] uppercase text-[#FAD6C6] mb-4">Our Services</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#F5F2EB]">Creative Excellence</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="luxury-card rounded-xl p-8"
              >
                <service.icon className="w-10 h-10 text-[#FAD6C6] mb-6" />
                <h3 className="font-serif text-xl text-[#F5F2EB] mb-3">{service.title}</h3>
                <p className="text-[#A3988F] text-sm">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Video Section */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="relative rounded-xl overflow-hidden aspect-video group cursor-pointer">
                <img
                  src="https://images.pexels.com/photos/2873486/pexels-photo-2873486.jpeg"
                  alt="ARTERYVAIL Media"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-[#0A0A0A]/40 flex items-center justify-center group-hover:bg-[#0A0A0A]/30 transition-colors">
                  <div className="w-20 h-20 bg-[#FAD6C6]/20 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play className="w-8 h-8 text-[#F5F2EB] ml-1" />
                  </div>
                </div>
                <div className="absolute bottom-4 left-4 right-4 text-center">
                  <p className="text-xs text-[#A3988F] bg-black/50 px-3 py-1 rounded-full inline-block">
                    Video Coming Soon
                  </p>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <p className="text-sm tracking-[0.3em] uppercase text-[#FAD6C6] mb-4">Our Craft</p>
              <h2 className="font-serif text-4xl text-[#F5F2EB] mb-6">The Art of Visual Storytelling</h2>
              <p className="text-[#A3988F] leading-relaxed mb-6">
                At ARTERYVAIL Media, we don't just create content—we craft experiences. Our team of cinematographers, 
                photographers, and brand strategists bring decades of combined experience to every project.
              </p>
              <p className="text-[#A3988F] leading-relaxed mb-8">
                From luxury brand campaigns to corporate documentaries, our clients have seen their visions transformed 
                into compelling visual narratives that captivate audiences and drive results.
              </p>
              <a href="#booking" className="btn-primary inline-flex items-center space-x-3" data-testid="media-book-btn">
                <span>Book a Consultation</span>
                <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>
          </div>
        </div>
      </section>


      {/* Our Works - Video Gallery */}
      <section id="portfolio" className="section-padding bg-[#1A1514]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-sm tracking-[0.3em] uppercase text-[#FFCBA4] mb-4">See Your Dream.</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#F5F2EB]">Some of Our Works</h2>
          </motion.div>

          {/* Video Modal */}
          {activeVideo && (
            <div 
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
              onClick={() => setActiveVideo(null)}
            >
              <div className="relative w-full max-w-4xl aspect-video" onClick={(e) => e.stopPropagation()}>
                <button 
                  onClick={() => setActiveVideo(null)}
                  className="absolute -top-12 right-0 text-white hover:text-[#FFCBA4] text-lg"
                >
                  Close ✕
                </button>
                <iframe
                  src={activeVideo.url}
                  className="w-full h-full rounded-xl"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={activeVideo.title}
                />
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mediaVideos.map((video, index) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                onClick={() => setActiveVideo(video)}
                className="relative rounded-xl overflow-hidden aspect-video group cursor-pointer"
              >
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/80 via-[#0A0A0A]/30 to-transparent" />
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="w-16 h-16 bg-[#FAD6C6]/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-3 group-hover:bg-[#FAD6C6]/40 group-hover:scale-110 transition-all">
                    <Play className="w-6 h-6 text-white ml-1" />
                  </div>
                  <p className="text-sm font-medium text-[#F5F2EB]">{video.title}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Rate Cards */}
      <section className="section-padding">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="font-serif text-4xl md:text-5xl text-[#F5F2EB] mb-6">Rate Cards</h2>
            <p className="text-lg text-[#A3988F] max-w-2xl mx-auto mb-8">
              We care to give you best work at the best budget.
            </p>
            
            {quoteSubmitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="luxury-card rounded-xl p-8"
              >
                <div className="w-16 h-16 bg-[#FAD6C6]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Calendar className="w-8 h-8 text-[#FAD6C6]" />
                </div>
                <h3 className="font-serif text-2xl text-[#F5F2EB] mb-3">Quote Request Received!</h3>
                <p className="text-[#A3988F]">We'll review your request and get back to you within 24 hours.</p>
              </motion.div>
            ) : (
              <form onSubmit={handleQuoteSubmit} className="luxury-card rounded-xl p-8 text-left space-y-6">
                <h3 className="font-serif text-2xl text-[#F5F2EB] mb-6 text-center">Get a Quote</h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-[#A3988F] mb-2">Full Name *</label>
                    <input
                      type="text"
                      value={quoteForm.name}
                      onChange={(e) => setQuoteForm({...quoteForm, name: e.target.value})}
                      className="luxury-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#A3988F] mb-2">Email *</label>
                    <input
                      type="email"
                      value={quoteForm.email}
                      onChange={(e) => setQuoteForm({...quoteForm, email: e.target.value})}
                      className="luxury-input"
                      required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-[#A3988F] mb-2">Phone *</label>
                    <input
                      type="tel"
                      value={quoteForm.phone}
                      onChange={(e) => setQuoteForm({...quoteForm, phone: e.target.value})}
                      className="luxury-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#A3988F] mb-2">Service Needed *</label>
                    <select
                      value={quoteForm.service}
                      onChange={(e) => setQuoteForm({...quoteForm, service: e.target.value})}
                      className="luxury-select"
                      required
                    >
                      <option value="">Select a service</option>
                      <option value="Film Production">Film Production</option>
                      <option value="Photography">Photography</option>
                      <option value="Branding & Design">Branding & Design</option>
                      <option value="Consultation">Consultation</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-[#A3988F] mb-2">Budget Range (Optional)</label>
                  <input
                    type="text"
                    value={quoteForm.budget}
                    onChange={(e) => setQuoteForm({...quoteForm, budget: e.target.value})}
                    className="luxury-input"
                    placeholder="e.g., $1,000 - $5,000"
                  />
                </div>

                <div>
                  <label className="block text-sm text-[#A3988F] mb-2">Project Details *</label>
                  <textarea
                    value={quoteForm.details}
                    onChange={(e) => setQuoteForm({...quoteForm, details: e.target.value})}
                    className="luxury-input h-32 resize-none"
                    placeholder="Tell us about your project, timeline, and specific requirements..."
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full flex items-center justify-center space-x-2"
                >
                  {loading ? (
                    <div className="loader w-5 h-5" />
                  ) : (
                    <>
                      <ArrowRight className="w-4 h-4" />
                      <span>Submit Quote Request</span>
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* Reviews */}
      <section className="section-padding bg-[#1A1514]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-sm tracking-[0.3em] uppercase text-[#FAD6C6] mb-4">Testimonials</p>
            <h2 className="font-serif text-4xl text-[#F5F2EB]">Client Reviews</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {reviews.map((review, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="luxury-card rounded-xl p-8"
              >
                <div className="flex space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${i < review.rating ? 'star-filled fill-current' : 'star-empty'}`}
                    />
                  ))}
                </div>
                <p className="text-[#F5F2EB] text-lg mb-6">"{review.comment}"</p>
                <div className="flex justify-between items-center">
                  <span className="text-[#FAD6C6] font-semibold">{review.name}</span>
                  <span className="text-sm text-[#A3988F]">{review.service}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking */}
      <section id="booking" className="section-padding">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-sm tracking-[0.3em] uppercase text-[#FAD6C6] mb-4">Book Now</p>
            <h2 className="font-serif text-4xl text-[#F5F2EB]">Schedule a Consultation</h2>
          </motion.div>

          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-12"
            >
              <div className="w-20 h-20 bg-[#FAD6C6]/10 rounded-full flex items-center justify-center mx-auto mb-8">
                <Calendar className="w-10 h-10 text-[#FAD6C6]" />
              </div>
              <h3 className="font-serif text-3xl text-[#F5F2EB] mb-4">Consultation Booked!</h3>
              <p className="text-[#A3988F]">Check your email for confirmation details.</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm text-[#A3988F] mb-2">Service Interest</label>
                  <select
                    value={bookingForm.service_type}
                    onChange={(e) => setBookingForm({...bookingForm, service_type: e.target.value})}
                    className="luxury-select"
                    data-testid="media-service-select"
                  >
                    <option value="media_consultation">General Consultation</option>
                    <option value="film_production">Film Production</option>
                    <option value="photography">Photography</option>
                    <option value="branding">Branding & Design</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-[#A3988F] mb-2">Preferred Date</label>
                  <input
                    type="date"
                    value={bookingForm.date}
                    onChange={(e) => setBookingForm({...bookingForm, date: e.target.value})}
                    min={minDateStr}
                    className="luxury-input"
                    required
                    data-testid="media-date-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-[#A3988F] mb-2">Preferred Time</label>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                  {timeSlots.map((slot) => (
                    <button
                      key={slot}
                      type="button"
                      onClick={() => setBookingForm({...bookingForm, time_slot: slot})}
                      className={`p-3 rounded-lg text-sm transition-all ${
                        bookingForm.time_slot === slot
                          ? 'bg-[#FAD6C6] text-[#0A0A0A]'
                          : 'bg-[#1A1514] text-[#A3988F] hover:bg-[#FAD6C6]/10'
                      }`}
                      data-testid={`media-time-${slot.replace(/[: ]/g, '-')}`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm text-[#A3988F] mb-2">Full Name</label>
                  <input
                    type="text"
                    value={bookingForm.name}
                    onChange={(e) => setBookingForm({...bookingForm, name: e.target.value})}
                    className="luxury-input"
                    required
                    data-testid="media-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#A3988F] mb-2">Email</label>
                  <input
                    type="email"
                    value={bookingForm.email}
                    onChange={(e) => setBookingForm({...bookingForm, email: e.target.value})}
                    className="luxury-input"
                    required
                    data-testid="media-email-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-[#A3988F] mb-2">Phone</label>
                <input
                  type="tel"
                  value={bookingForm.phone}
                  onChange={(e) => setBookingForm({...bookingForm, phone: e.target.value})}
                  className="luxury-input"
                  required
                  data-testid="media-phone-input"
                />
              </div>

              <div>
                <label className="block text-sm text-[#A3988F] mb-2">Project Details</label>
                <textarea
                  value={bookingForm.notes}
                  onChange={(e) => setBookingForm({...bookingForm, notes: e.target.value})}
                  className="luxury-input h-32 resize-none"
                  placeholder="Tell us about your project or creative vision..."
                  data-testid="media-notes-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading || !bookingForm.time_slot}
                className="btn-primary w-full flex items-center justify-center space-x-2"
                data-testid="media-submit-btn"
              >
                {loading ? (
                  <div className="loader w-5 h-5" />
                ) : (
                  <>
                    <Calendar className="w-4 h-4" />
                    <span>Book Consultation</span>
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default MediaPage;
